import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useI18n } from '../i18n';
import { company } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [draft, setDraft] = useState(null);
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, answer: '' });
  const [captchaError, setCaptchaError] = useState(false);

  const refreshCaptcha = () => {
    setCaptcha({
      a: Math.floor(Math.random() * 8) + 2,
      b: Math.floor(Math.random() * 8) + 1,
      answer: ''
    });
    setCaptchaError(false);
  };

  useEffect(() => {
    refreshCaptcha();
    try {
      const d = localStorage.getItem('ha_booking_draft');
      if (d) setDraft(JSON.parse(d));
    } catch (_) {}
  }, []);

  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = (e) => {
    e.preventDefault();
    if (parseInt(captcha.answer, 10) !== captcha.a + captcha.b) {
      setCaptchaError(true);
      toast({ title: lang === 'fr' ? 'Captcha incorrect' : 'Wrong captcha', description: lang === 'fr' ? 'Merci de vérifier le calcul.' : 'Please check the answer.' });
      return;
    }
    const subject = encodeURIComponent(`[Ha Transport] Demande de ${form.name}`);
    const lines = [
      `Nom: ${form.name}`,
      `Email: ${form.email}`,
      `Téléphone: ${form.phone}`,
      '',
      `Message:`,
      form.message,
      ''
    ];
    if (draft) {
      lines.push('--- Réservation ---');
      Object.entries(draft).forEach(([k, v]) => lines.push(`${k}: ${v}`));
    }
    const body = encodeURIComponent(lines.join('\n'));
    window.location.href = `mailto:${company.email}?subject=${subject}&body=${body}`;
    toast({ title: t('contact.sent') });
    setForm({ name: '', email: '', phone: '', message: '' });
    try { localStorage.removeItem('ha_booking_draft'); } catch (_) {}
    setDraft(null);
    refreshCaptcha();
  };

  return (
    <div className="pt-[72px] bg-white">
      <section className="bg-black text-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.4em] uppercase text-white/60">Contact</p>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 max-w-3xl">{t('contact.title')}</h1>
          <p className="text-white/70 mt-5 max-w-2xl">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
          <div>
            <h2 className="font-serif text-3xl">{lang === 'fr' ? 'Nos coordonnées' : 'Get in touch'}</h2>
            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-black/15 flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
                <div><p className="text-xs uppercase tracking-widest text-black/50">{lang === 'fr' ? 'Adresse' : 'Address'}</p><p className="mt-1">{company.address}</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-black/15 flex items-center justify-center"><Phone className="w-5 h-5" /></div>
                <div><p className="text-xs uppercase tracking-widest text-black/50">{lang === 'fr' ? 'Téléphone' : 'Phone'}</p><a href={`tel:${company.phone.replace(/\s/g, '')}`} className="mt-1 block hover:underline">{company.phone}</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-black/15 flex items-center justify-center"><Mail className="w-5 h-5" /></div>
                <div><p className="text-xs uppercase tracking-widest text-black/50">Email</p><a href={`mailto:${company.email}`} className="mt-1 block hover:underline">{company.email}</a></div>
              </div>
            </div>
            <div className="mt-10 aspect-[16/10] overflow-hidden">
              <iframe
                title="map"
                src="https://www.google.com/maps?q=3+Avenue+Charles+de+Gaulle,+95700+Roissy-en-France&output=embed"
                className="w-full h-full border-0 grayscale"
                loading="lazy"
              />
            </div>
          </div>

          <div className="bg-[#f6f4f1] p-8 lg:p-10">
            <h2 className="font-serif text-3xl">{lang === 'fr' ? 'Envoyez-nous un message' : 'Send us a message'}</h2>
            {draft && (
              <div className="mt-4 text-xs text-black/60 border border-black/10 bg-white p-3">
                {lang === 'fr' ? 'Brouillon de réservation joint :' : 'Booking draft attached:'} {draft.from} → {draft.to || draft.duration + 'h'} • {draft.date} {draft.time}
              </div>
            )}
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-black/60">{t('contact.name')}</label>
                <Input required value={form.name} onChange={(e) => onChange('name', e.target.value)} className="mt-1 rounded-none bg-white border-black/15" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-black/60">{t('contact.email')}</label>
                  <Input required type="email" value={form.email} onChange={(e) => onChange('email', e.target.value)} className="mt-1 rounded-none bg-white border-black/15" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-black/60">{t('contact.phone')}</label>
                  <Input value={form.phone} onChange={(e) => onChange('phone', e.target.value)} className="mt-1 rounded-none bg-white border-black/15" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-black/60">{t('contact.message')}</label>
                <Textarea required rows={6} value={form.message} onChange={(e) => onChange('message', e.target.value)} className="mt-1 rounded-none bg-white border-black/15" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-black/60">
                  {lang === 'fr' ? 'V\u00e9rification anti-spam' : 'Anti-spam check'}
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <span className="font-serif text-lg bg-white border border-black/15 px-4 h-10 flex items-center select-none">
                    {captcha.a} + {captcha.b} = ?
                  </span>
                  <Input
                    required
                    type="number"
                    inputMode="numeric"
                    value={captcha.answer}
                    onChange={(e) => { setCaptcha({ ...captcha, answer: e.target.value }); setCaptchaError(false); }}
                    placeholder="?"
                    className={`rounded-none bg-white w-24 ${captchaError ? 'border-red-500' : 'border-black/15'}`}
                  />
                  <button type="button" onClick={refreshCaptcha} className="text-xs uppercase tracking-widest text-black/60 hover:text-black underline">
                    {lang === 'fr' ? 'Changer' : 'Refresh'}
                  </button>
                </div>
              </div>
              <Button type="submit" className="bg-black hover:bg-black/85 text-white rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">
                <Send className="w-4 h-4 mr-2" /> {t('contact.send')}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
